/* =====================================================================
 * DM Conversation API Client — 팀 DM 대화 REST API
 * ===================================================================== */

import type { Conversation, UserMessage } from '@auw/workspace-utils/types/dm';
import { authFetch, getHeaders, API_BASE_URL } from './authFetch';

const BASE = () => `${API_BASE_URL}/api/v1/dm/conversations`;

/* ------------------------------------------------------------------ */
/*  대화 목록 / 생성 / 상세                                            */
/* ------------------------------------------------------------------ */

export async function listConversations(): Promise<{ conversations: Conversation[] }> {
  const res = await authFetch(BASE(), { headers: getHeaders() });
  if (!res.ok) throw new Error(`list conversations failed (${res.status})`);
  return res.json();
}

export async function createDirectConversation(
  teamId: string,
  targetUserId: string,
): Promise<Conversation> {
  const res = await authFetch(BASE(), {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      type: 'direct',
      team_id: teamId,
      target_user_id: targetUserId,
    }),
  });
  if (!res.ok) throw new Error(`create direct conversation failed (${res.status})`);
  return res.json();
}

export async function createGroupConversation(
  teamId: string,
  name: string,
  memberIds: string[],
): Promise<Conversation> {
  const res = await authFetch(BASE(), {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      type: 'group',
      team_id: teamId,
      name,
      member_ids: memberIds,
    }),
  });
  if (!res.ok) throw new Error(`create group conversation failed (${res.status})`);
  return res.json();
}

export async function getConversation(conversationId: string): Promise<Conversation> {
  const res = await authFetch(`${BASE()}/${conversationId}`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`get conversation failed (${res.status})`);
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  메시지                                                             */
/* ------------------------------------------------------------------ */

export async function getConversationMessages(
  conversationId: string,
  limit = 50,
  before?: string,
): Promise<{ messages: UserMessage[]; has_more: boolean }> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (before) params.set('before', before);
  const res = await authFetch(`${BASE()}/${conversationId}/messages?${params}`, {
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`get messages failed (${res.status})`);
  return res.json();
}

export async function sendConversationMessage(
  conversationId: string,
  content: string,
  contentType = 'text',
  metadata?: Record<string, unknown>,
): Promise<UserMessage> {
  const res = await authFetch(`${BASE()}/${conversationId}/messages`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ content, content_type: contentType, metadata }),
  });
  if (!res.ok) throw new Error(`send message failed (${res.status})`);
  return res.json();
}

export interface ReactionToggleResult {
  action: 'added' | 'removed';
  emoji: string;
  reactions: Array<{ emoji: string; userIds: string[] }>;
}

/** 메시지 리액션 토글 — 서버가 멤버 전원에게 user_reaction 이벤트로 전파한다. */
export async function toggleMessageReaction(
  conversationId: string,
  messageId: string,
  emoji: string,
): Promise<ReactionToggleResult> {
  const res = await authFetch(
    `${BASE()}/${conversationId}/messages/${messageId}/reactions`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ emoji }),
    },
  );
  if (!res.ok) throw new Error(`toggle reaction failed (${res.status})`);
  return res.json();
}

export async function uploadConversationFiles(
  conversationId: string,
  files: File[],
): Promise<{
  uploaded_files: Array<{
    file_id: string;
    name: string;
    mime_type: string;
    size: number;
  }>;
  count: number;
  errors?: Array<{ file: string; error: string }> | null;
}> {
  const form = new FormData();
  for (const file of files) {
    form.append('files', file);
  }
  const res = await authFetch(`${BASE()}/${conversationId}/files`, {
    method: 'POST',
    headers: getHeaders(false),
    body: form,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`upload conversation files failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  읽음 처리                                                          */
/* ------------------------------------------------------------------ */

export async function markConversationRead(
  conversationId: string,
): Promise<{ success: boolean }> {
  const res = await authFetch(`${BASE()}/${conversationId}/read`, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`mark read failed (${res.status})`);
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  멤버 관리 (그룹)                                                   */
/* ------------------------------------------------------------------ */

export async function addConversationMember(
  conversationId: string,
  userId: string,
  systemEvent?: string,
): Promise<Record<string, unknown>> {
  const res = await authFetch(`${BASE()}/${conversationId}/members`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ user_id: userId, system_event: systemEvent }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`add member failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function removeConversationMember(
  conversationId: string,
  userId: string,
): Promise<{ success: boolean }> {
  const res = await authFetch(`${BASE()}/${conversationId}/members/${userId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`remove member failed (${res.status})`);
  return res.json();
}

export async function leaveConversation(
  conversationId: string,
): Promise<{ left: boolean; conversation_id: string; conversation_deleted: boolean }> {
  const res = await authFetch(`${BASE()}/${conversationId}/leave`, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`leave conversation failed (${res.status})`);
  return res.json();
}
